//src/components/FormSettings/FormSubmitSettings/SessionSubmissionTable.jsx
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

import { useAuth } from '../../../contexts/AuthContext';
import { useFormSubmissions } from '../../../contexts/FormSubmissionsContext';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';

import useSubmissionProviderOutputHydration from '../../../hooks/useSubmissionProviderOutputHydration';
import SubmissionTableBase from './SubmissionTableBase';

const toMs = (t) => {
  try {
    if (!t) return 0;
    if (typeof t?.toDate === 'function') return t.toDate().getTime();
    if (t instanceof Date) return t.getTime();
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? ms : 0;
  } catch (_) {
    return 0;
  }
};

const SessionSubmissionTable = () => {
  const { currentUser } = useAuth();
  const getAuthToken = useCallback(
    () => currentUser?.getIdToken?.() ?? null,
    [currentUser]
  );

  const { userSubmissions = [] } = useFormSubmissions();
  const { submissionSettings } = useFormSubmissionSettings();

  const action = submissionSettings?.addToSubmissionsTable || {};
  const columnOrder = action.columnOrder || [];
  const columnWidths = action.columnWidths || {};
  const stickyHeader = !!action.enableStickyHeader;

  const newestFirst = useMemo(() => {
    const rows = Array.isArray(userSubmissions) ? userSubmissions : [];
    return rows
      .slice()
      .sort((a, b) => {
        const aMs = toMs(a?.submittedAt) || toMs(a?.createdAt);
        const bMs = toMs(b?.submittedAt) || toMs(b?.createdAt);
        return bMs - aMs;
      });
  }, [userSubmissions]);

  // Footer pagination state (session-only)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Keep page valid if new rows come in / rowsPerPage changes
  useEffect(() => {
    setPage(0);
  }, [rowsPerPage, newestFirst.length]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return newestFirst.slice(start, start + rowsPerPage);
  }, [newestFirst, page, rowsPerPage]);

  // Display Table provider-output hydration.
  //
  // Enterprise integration rule:
  // - This listens only to the currently visible/paged submission docs.
  // - It does not call Bitly.
  // - It does not fetch metrics.
  // - It does not add broad listeners to FormSubmissionsContext.
  // - It hydrates backend-enriched shortlinks.bitly output into row copies.
  const shouldHydrateBitly =
    action?.enabled === true &&
    action?.enableBitly === true;

  const {
    rows: hydratedPaged,
  } = useSubmissionProviderOutputHydration({
    enabled: shouldHydrateBitly,
    provider: 'bitly',
    rows: paged,
  });

  if (!newestFirst.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          No submissions in this session yet.
        </Typography>
      </Box>
    );
  }

  return (
    <SubmissionTableBase
      rows={hydratedPaged}
      allowStubRows={false}
      enablePagination={true}
      page={page}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      totalCount={newestFirst.length}
      columnOrder={columnOrder}
      columnWidths={columnWidths}
      stickyHeader={stickyHeader}
      getAuthToken={getAuthToken}
      maxHeight={600}
    />
  );
};

export default React.memo(SessionSubmissionTable);
